import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { BRANDS } from "@/data/content";
import { SITE } from "@/config/site";
import { sendForm } from "@/lib/sendForm";
import GdprLabel from "@/components/GdprLabel";
import FormError from "@/components/FormError";
import { translate } from "@/i18n";
import { useLanguage } from "@/i18n/LanguageContext";

const inputCls = "w-full bg-[#121316] border border-white/10 px-4 py-3 text-sm text-white font-light focus:border-[#C2A675] focus:outline-none transition-colors";
const labelCls = "block text-[11px] uppercase tracking-[0.16em] text-[#A8A6A0] mb-2";
const ASSUNTOS = ["info", "amostras", "orcamento", "outro"];

export default function Contactos() {
  const { t, lang } = useLanguage();
  const [params] = useSearchParams();
  const marca = params.get("marca");
  const marcaName = BRANDS.find((b) => b.slug === marca)?.name;
  const assuntoParam = params.get("assunto");
  const assuntoInit = ASSUNTOS.includes(assuntoParam) ? assuntoParam : marcaName ? "info" : "";

  const [form, setForm] = useState({ nome: "", empresa: "", atividade: "", assunto: assuntoInit, email: "", telefone: "", mensagem: marcaName ? `${marcaName} — ` : "", consentimento: false, website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);
  const startedAt = useRef(Date.now());

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(false);
    try {
      await sendForm({
        form: "contacto",
        lang,
        honeypot: form.website,
        startedAt: startedAt.current,
        fields: {
          assunto: translate("pt", `contact.assuntos.${form.assunto}`),
          nome: form.nome, empresa: form.empresa, email: form.email, telefone: form.telefone,
          atividade: form.atividade, mensagem: form.mensagem,
        },
      });
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError(true); // os dados preenchidos mantêm-se
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contacts-page" className="pt-20">
      <Seo title={t("seo.contacts.title")} description={t("seo.contacts.desc")} />

      <section className="pt-16 lg:pt-20 pb-16 lg:pb-20 max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
        <Reveal className="lg:col-span-5">
          <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("contact.tag")}</span>
          <h1 className="serif-display text-4xl sm:text-5xl font-light text-white mt-4 mb-6 leading-tight">{t("contact.title")}</h1>
          <p className="text-base text-[#A8A6A0] font-light leading-relaxed mb-10">{t("contact.sub")}</p>

          <ul className="flex flex-wrap gap-2 mb-10" data-testid="contact-pathways">
            {ASSUNTOS.filter((a) => a !== "outro").map((a) => (
              <li key={a}>
                <button type="button" onClick={() => setForm((f) => ({ ...f, assunto: a }))} data-testid={`contact-pathway-${a}`} className={`px-3 py-1.5 border text-[10px] uppercase tracking-[0.14em] transition-colors ${form.assunto === a ? "border-[#C2A675] text-[#C2A675]" : "border-white/15 text-[#D6D3CC] hover:border-white/40"}`}>
                  {t(`contact.assuntos.${a}`)}
                </button>
              </li>
            ))}
          </ul>

          <div className="space-y-6 text-sm">
            <div>
              <span className="block text-[11px] uppercase tracking-[0.18em] text-white mb-1">{t("contact.email_label")}</span>
              <a href={`mailto:${SITE.email}`} data-testid="contact-email-link" className="text-[#E8E6E1] font-light hover:text-[#C2A675] transition-colors">
                {SITE.email}
              </a>
            </div>
            <div>
              <span className="block text-[11px] uppercase tracking-[0.18em] text-white mb-1">{t("contact.phone_label")}</span>
              <a href={SITE.phoneHref} data-testid="contact-phone-link" className="text-[#E8E6E1] font-light hover:text-[#C2A675] transition-colors">
                {SITE.phone}
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <div className="bg-[#17191E] p-8 md:p-10 border border-white/10">
            <h2 className="serif-display text-2xl font-light text-white mb-8">{t("contact.form_title")}</h2>
            {sent ? (
              <div data-testid="contact-success" role="status">
                <p className="serif-display text-2xl font-light text-[#C2A675]">{t("form.success_title")}</p>
                <p className="text-sm text-[#A8A6A0] font-light mt-3 leading-relaxed">{t("form.success_text")}</p>
                <button
                  type="button"
                  onClick={() => { setSent(false); startedAt.current = Date.now(); setForm({ nome: "", empresa: "", atividade: "", assunto: "", email: "", telefone: "", mensagem: "", consentimento: false, website: "" }); }}
                  className="mt-8 px-8 py-4 border border-[#C2A675]/60 text-[#C2A675] text-xs uppercase tracking-[0.18em] hover:bg-[#C2A675] hover:text-[#121316] transition-colors"
                >
                  {t("form.another")}
                </button>
              </div>
            ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set("website")} className="hidden" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="c-nome" className={labelCls}>{t("form.nome")} *</label>
                  <input id="c-nome" data-testid="contact-name-input" type="text" required minLength={2} value={form.nome} onChange={set("nome")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="c-empresa" className={labelCls}>{t("form.empresa")}</label>
                  <input id="c-empresa" data-testid="contact-company-input" type="text" value={form.empresa} onChange={set("empresa")} className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="c-email" className={labelCls}>{t("form.email")} *</label>
                  <input id="c-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="c-telefone" className={labelCls}>{t("form.telefone")}</label>
                  <input id="c-telefone" data-testid="contact-phone-input" type="tel" value={form.telefone} onChange={set("telefone")} className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="c-assunto" className={labelCls}>{t("contact.assunto")} *</label>
                  <select id="c-assunto" data-testid="contact-subject-select" required value={form.assunto} onChange={set("assunto")} className={inputCls}>
                    <option value="">—</option>
                    {ASSUNTOS.map((a) => <option key={a} value={a}>{t(`contact.assuntos.${a}`)}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="c-atividade" className={labelCls}>{t("form.atividade")}</label>
                  <select id="c-atividade" data-testid="contact-activity-select" value={form.atividade} onChange={set("atividade")} className={inputCls}>
                    <option value="">—</option>
                    {t("form.atividades").map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="c-mensagem" className={labelCls}>{t("form.mensagem")} *</label>
                <textarea id="c-mensagem" data-testid="contact-message-input" rows={4} required minLength={5} value={form.mensagem} onChange={set("mensagem")} className={inputCls} />
              </div>
              <div className="flex items-start gap-3">
                <input id="c-gdpr" data-testid="contact-gdpr-checkbox" type="checkbox" required checked={form.consentimento} onChange={set("consentimento")} className="mt-1 accent-[#C2A675]" />
                <GdprLabel htmlFor="c-gdpr" />
              </div>
              {error && <FormError />}
              <button type="submit" disabled={sending} data-testid="contact-submit-button" className="w-full py-4 bg-[#C2A675] text-[#121316] font-medium text-xs uppercase tracking-[0.2em] hover:bg-white transition-colors disabled:opacity-50">
                {sending ? t("common.sending") : t("form.send")}
              </button>
            </form>
            )}
          </div>
        </Reveal>
      </section>

    </div>
  );
}
